import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';

/**
 * Whether the signed-in user has GitHub credentials for the code-insights
 * cards, plus a way to obtain them.
 *
 * The state is shared across every caller: four cards and the sign-in card ask
 * the same question on a Repository page, and the answer has to flip for all of
 * them the moment one of them completes the sign-in. `scmAuthApi` holds no
 * observable session state of its own, so the silent probe is run once and its
 * result kept in a module-level store.
 */
type Listener = () => void;

let signedIn = false;
let probe: Promise<void> | undefined;
const listeners = new Set<Listener>();

function publish(next: boolean) {
  if (signedIn === next) {
    return;
  }
  signedIn = next;
  listeners.forEach(listener => listener());
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The GitHub scope the code-insights cards read repository content with. */
const GITHUB_SCOPE = { customScopes: { github: ['repo'] } };

export function useGithubSignedIn(hostname: string = 'github.com') {
  const scmAuth = useApi(scmAuthApiRef);
  const url = `https://${hostname}`;
  const isSignedIn = useSyncExternalStore(subscribe, () => signedIn);

  /*
   * `optional: true` never opens a popup: it resolves with no token when the
   * user has not granted the scope, which is exactly the signed-out case.
   */
  useEffect(() => {
    if (signedIn || probe) {
      return;
    }
    probe = scmAuth
      .getCredentials({ url, additionalScope: GITHUB_SCOPE, optional: true })
      .then(credentials => publish(Boolean(credentials?.token)))
      .catch(() => publish(false));
  }, [scmAuth, url]);

  const signIn = useCallback(async () => {
    const credentials = await scmAuth.getCredentials({
      url,
      additionalScope: GITHUB_SCOPE,
    });
    publish(Boolean(credentials?.token));
  }, [scmAuth, url]);

  return { isSignedIn, signIn };
}
